<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import ArticleTip from "@/components/ArticleTip";
import ArticleAnnounce from "@/components/ArticleAnnounce";
import { articleContent } from "@/data/articleContent";
import { articles } from "@/data/articles";

const route = useRoute();
const article = computed(() => articleContent[route.params.slug]);
const relatedArticles = computed(() =>
  article.value ? { 1: articles[article.value.related] } : {}
);

// Картинки статьи лежат в app/assets/img/article/<folder>/
const img = (name) =>
  require(`@/assets/img/article/${article.value.folder}/${name}`);

// Первую картинку статьи грузим сразу — она попадает на первый экран и
// обычно является самым крупным его элементом. Остальные — лениво.
const firstImageIndex = computed(() =>
  article.value
    ? article.value.blocks.findIndex(
        (block) => block.type === 'image' || block.type === 'sectionImage'
      )
    : -1
);

const imgLoading = (index) => (index === firstImageIndex.value ? 'eager' : 'lazy');
</script>
<template>
  <div class="article" v-if="article">
    <div class="article__inner">
      <div class="article__main">
        <div class="article__header">
          <h1 class="article__title" v-html="article.title"></h1>
          <h3 class="article__subtitle" v-html="article.subtitle"></h3>
        </div>

        <div class="article__content-wrap">

          <template v-for="(block, i) in article.blocks" :key="i">
            <img
              v-if="block.type === 'image'"
              :src="img(block.src)"
              alt=""
              class="article__image"
              :loading="imgLoading(i)"
            >

            <div
              v-else-if="block.type === 'section'"
              class="article__section"
              :class="{ article__section_summary: block.summary }"
            >
              <article-tip v-if="block.tip" :text="block.tip" absolute/>
              <div class="article__section-inner">
                <h2 v-html="block.heading"></h2>
                <div v-html="block.content"></div>
              </div>
            </div>

            <div
              v-else-if="block.type === 'infographics'"
              class="article__section"
            >
              <div class="article__section-inner">
                <h2 v-html="block.heading"></h2>
                <div class="article__infographics">
                  <template v-for="(item, j) in block.items" :key="j">
                    <span v-if="j > 0" class="_divider"></span>
                    <div>
                      <img :src="img(item.icon)" alt="" loading="lazy">
                      <span v-html="item.text"></span>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div
              v-else-if="block.type === 'sectionImage'"
              class="article__section-wrap"
            >
              <article-tip v-if="block.tip" :text="block.tip"/>
              <img
                v-if="block.side === 'left'"
                :src="img(block.image)"
                alt=""
                class="article__image"
                :loading="imgLoading(i)"
              >
              <div class="article__section">
                <div class="article__section-inner">
                  <h2 v-html="block.heading"></h2>
                  <div v-html="block.content"></div>
                </div>
              </div>
              <img
                v-if="block.side === 'right'"
                :src="img(block.image)"
                alt=""
                class="article__image"
                :loading="imgLoading(i)"
              >
            </div>

            <div
                v-else-if="block.type === 'header'"
                class="article__content-header"
            >
              <h2 v-html="block.title"></h2>
              <p v-if="block.text" v-html="block.text"></p>
            </div>

            <div
                v-else-if="block.type === 'sectionDouble'"
                class="article__section-wrap"
            >
              <article-tip v-if="block.tip" :text="block.tip"/>
              <div v-for="subBlock in block.subsections" class="article__section" :key="subBlock.heading">
                <div class="article__section-inner">
                  <h2 v-html="subBlock.heading"></h2>
                  <div v-html="subBlock.content"></div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="article__bottom">
      <img src="@/assets/img/decor5.png" alt="" class="_decor" loading="lazy">
      <article-announce v-for="block in relatedArticles" :key="block.title" :data="block" :color="block.bg"/>
    </div>
  </div>
</template>
